/**
 * Utilitaires de tri pour lieux et objets
 */

import { normalizeString } from './stringUtils';

export const sortItems = <T extends Record<string, any>>(
  items: T[],
  sortBy: string
): T[] => {
  if (!sortBy || sortBy === 'none') return items;

  const [field, order = 'asc'] = sortBy.split('-');

  return [...items].sort((a, b) => {
    let compareA: any;
    let compareB: any;

    // Gestion des chemins imbriqués (ex: 'piece.batiment.site.nom')
    if (field.includes('.')) {
      const path = field.split('.');
      compareA = path.reduce((obj, key) => obj?.[key], a);
      compareB = path.reduce((obj, key) => obj?.[key], b);
    } else {
      compareA = a[field];
      compareB = b[field];
    }

    // Gestion des valeurs nulles/undefined
    if (compareA == null) return 1;
    if (compareB == null) return -1;

    // Normalisation pour tri insensible casse et accents
    if (typeof compareA === 'string' && typeof compareB === 'string') {
      compareA = normalizeString(compareA);
      compareB = normalizeString(compareB);
    }

    // Comparaison
    let result = 0;
    if (compareA < compareB) result = -1;
    else if (compareA > compareB) result = 1;

    return order === 'desc' ? -result : result;
  });
};

/**
 * Options de tri prédéfinies
 */
export const LIEUX_SORT_OPTIONS = [
  { value: 'nom-asc', label: '📝 Nom (A→Z)', field: 'nom' },
  { value: 'nom-desc', label: '📝 Nom (Z→A)', field: 'nom' },
  { value: 'code-asc', label: '🔢 Code (A→Z)', field: 'code' },
  { value: 'site-asc', label: '📍 Site (A→Z)', field: 'piece.batiment.site.nom' },
  { value: 'theme-asc', label: '🎨 Thème (A→Z)', field: 'theme' },
  { value: 'recent', label: '🕒 Plus récents', field: 'createdAt', order: 'desc' },
  { value: 'ancien', label: '📅 Plus anciens', field: 'createdAt', order: 'asc' },
];

export const OBJETS_SORT_OPTIONS = [
  { value: 'nom-asc', label: '📝 Nom (A→Z)', field: 'nom' },
  { value: 'nom-desc', label: '📝 Nom (Z→A)', field: 'nom' },
  { value: 'categorie-asc', label: '📁 Catégorie (A→Z)', field: 'categorie' },
  { value: 'etat-asc', label: '⚙️ État (A→Z)', field: 'etat' },
  { value: 'quantite-desc', label: '🔢 Quantité (↓)', field: 'quantite' },
  { value: 'quantite-asc', label: '🔢 Quantité (↑)', field: 'quantite' },
  { value: 'recent', label: '🕒 Plus récents', field: 'createdAt', order: 'desc' },
  { value: 'ancien', label: '📅 Plus anciens', field: 'createdAt', order: 'asc' },
];

/**
 * Tri avec options avancées
 */
export const sortItemsAdvanced = <T extends Record<string, any>>(
  items: T[],
  sortOption: { field: string; order?: 'asc' | 'desc' }
): T[] => {
  const { field, order = 'asc' } = sortOption;
  return sortItems(items, `${field}-${order}`);
};

